import React, { useRef, useEffect, useCallback, memo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import { selectShowPotCollectionIntro, selectPotCollectionRespinGameType } from "../../../State/selectors";
import { selectInfoPopupActive } from "../../../State/Slices/userInterfaceSlice";
import { safeInit, attemptFullscreenToggle } from "../../../utilities";
import TickerTimeout from "../../../utils/TickerTimeout";
import PotCollectionIntroController from "./PotCollectionIntroController";

// ─── PotCollectionIntroDialog ────────────────────────────────────────────────
// Slim React shell: Redux state, click/keyboard events, and lifecycle.
// All PIXI display objects (overlay, bg, logo, cards, text, animations) are
// managed by PotCollectionIntroController.
const PotCollectionIntroDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showPotCollectionIntro = useSelector(selectShowPotCollectionIntro);
  const respinGameType = useSelector(selectPotCollectionRespinGameType);
  const infoPopupActive = useSelector(selectInfoPopupActive);

  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolSize, device, orientation, isMobileSmall } = dimensionsContext;

  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  // Always holds the latest dimensions — read when the deferred (RAF) controller
  // construction runs, so a resize during the RAF gap isn't built from the stale
  // first-render dimensions captured by the init effect's [] closure.
  const dimensionsContextRef = useRef(dimensionsContext);
  dimensionsContextRef.current = dimensionsContext;
  const handleClickRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const delayRef = useRef(null);

  // ── Initialize controller once container is available ───────────────────
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new PotCollectionIntroController(
          containerRef.current,
          dimensionsContextRef.current,
          dispatch,
          t,
        );
        // Wire tap-to-start click handler (fullscreen + dismiss)
        controllerRef.current.setClickHandler((...args) => {
          if (handleClickRef.current) handleClickRef.current(...args);
        });
      });
    }
    return () => {
      if (debounceTimeoutRef.current) {
        debounceTimeoutRef.current.clear();
        debounceTimeoutRef.current = null;
      }
      if (delayRef.current) {
        delayRef.current.clear();
        delayRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Update controller dimensions on resize / orientation change ────────
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateDimensions(dimensionsContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device, orientation, isMobileSmall]);


  // ── Track when PC intro is waiting behind the FR info popup ────────────
  const pcIntroPendingRef = useRef(false);
  useEffect(() => {
    if (showPotCollectionIntro && infoPopupActive) {
      pcIntroPendingRef.current = true;
    }
  }, [showPotCollectionIntro, infoPopupActive]);

  // ── Start sequence when pot collection intro is triggered ──────────────
  // Gate on !infoPopupActive: if FR info popup ("You have X free rounds left")
  // is still open, wait for it to close. When it does, add a short delay
  // so the intro doesn't pop up instantly. If info popup was already closed
  // (normal flow), start immediately — same as original behavior.
  useEffect(() => {
    if (controllerRef.current && showPotCollectionIntro && !infoPopupActive) {
      const wasPending = pcIntroPendingRef.current;
      pcIntroPendingRef.current = false;

      if (wasPending) {
        // Was waiting behind info popup — ease in with a delay
        delayRef.current = new TickerTimeout(() => {
          controllerRef.current?.startSequence(respinGameType);
          soundManager.play(SOUND_NAME.bonus_intro_popup);
          delayRef.current = null;
        }, 1000);
        return () => {
          if (delayRef.current) {
            delayRef.current.clear();
            delayRef.current = null;
          }
        };
      } else {
        // Normal path — start immediately
        controllerRef.current.startSequence(respinGameType);
        soundManager.play(SOUND_NAME.bonus_intro_popup);
      }
    }
  }, [showPotCollectionIntro, respinGameType, infoPopupActive]);

  // ── Click handler (tap to start: fullscreen + dismiss) ─────────────────
  const handleClick = useCallback(
    async (evt) => {
      if (debounceTimeoutRef.current) return;
      debounceTimeoutRef.current = new TickerTimeout(() => {
        debounceTimeoutRef.current = null;
      }, 300);

      if (evt?.stopPropagation) evt.stopPropagation();

      // Trigger fullscreen on the user gesture
      await attemptFullscreenToggle(dispatch, device);

      if (controllerRef.current && showPotCollectionIntro) {
        const accepted = controllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showPotCollectionIntro, dispatch, device],
  );

  // Keep click handler ref current (avoids stale closures)
  handleClickRef.current = handleClick;

  // ── Spacebar listener ──────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) return;

      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function") event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
        return;
      }

      if (!showPotCollectionIntro || !controllerRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      handleClick(event);
    };

    if (showPotCollectionIntro) {
      document.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPotCollectionIntro]);


  // ── Render ─────────────────────────────────────────────────────────────
  // Container always mounts. Controller manages all PIXI children.
  // Overlay visibility/interactivity is handled by the controller.
  return (
    <Container
      ref={containerRef}
      name="pot_collection_intro"
      x={0}
      y={0}
      zIndex={100}
      sortableChildren
    />
  );
};

export default memo(PotCollectionIntroDialog);
